import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Divider,
  Space,
  Layout,
  Row,
  Col,
  Image,
  Checkbox,
  App
} from 'antd'
import { AppCard } from '@/shared/components/ui'
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  ShopOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../../stores/authStore'
import type { RegisterFormData } from '../../types/auth.types'
import { useTranslation, Trans } from 'react-i18next'

const { Title, Text, Paragraph } = Typography
const { Content } = Layout

const RegisterPage: React.FC = () => {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation('pages')

  const { register, isLoading: authLoading } = useAuthStore()

  // 验证用户名是否可用（模拟）
  const checkUsernameAvailability = async (username: string): Promise<boolean> => {
    // 这里应该调用API检查用户名可用性
    await new Promise(resolve => setTimeout(resolve, 300))
    return username !== 'admin' && username !== 'test'
  }

  // 验证密码强度
  const validatePasswordStrength = (password: string): {
    isValid: boolean
    message: string
  } => {
    const minLength = 8
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumbers = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)

    if (password.length < minLength) {
      return { isValid: false, message: t('register.passwordStrengthMinLength') }
    }

    if (!hasUpperCase || !hasLowerCase) {
      return { isValid: false, message: t('register.passwordStrengthUpperLower') }
    }

    if (!hasNumbers) {
      return { isValid: false, message: t('register.passwordStrengthNumbers') }
    }

    if (!hasSpecialChar) {
      return { isValid: false, message: t('register.passwordStrengthSpecial') }
    }

    return { isValid: true, message: t('register.passwordStrengthEnough') }
  }

  // 处理注册提交
  const handleSubmit = async (values: RegisterFormData) => {
    setLoading(true)
    setError(null)

    try {
      await register(values)
      // 注册成功后重定向到仪表板
      message.success(t('register.registerSuccess'))
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.registerError'))
    } finally {
      setLoading(false)
    }
  }

  // 处理表单提交失败
  const handleFinishFailed = (errorInfo: any) => {
  }

  // 跳转到登录页面
  const handleGoToLogin = () => {
    navigate('/login')
  }


  // 账户信息步骤
  const renderAccountStep = () => (
    <>
      <div style={{ marginBottom: '24px' }}>
        <Text strong style={{ fontSize: '16px' }}>
          <UserOutlined /> {t('register.accountInfo')}
        </Text>
      </div>

      {/* 用户名输入 */}
      <Form.Item
        name="username"
        label={t('register.username')}
        rules={[
          { required: true, message: t('register.usernameRequired') },
          { min: 3, message: t('register.usernameMinLength') },
          { max: 50, message: t('register.usernameMaxLength') },
          {
            validator: async (_, value) => {
              if (!value || value.length < 3) {
                return Promise.resolve()
              }
              const isAvailable = await checkUsernameAvailability(value)
              if (!isAvailable) {
                return Promise.reject(new Error(t('register.usernameTaken')))
              }
              return Promise.resolve()
            }
          }
        ]}
      >
        <Input
          size="large"
          prefix={<UserOutlined style={{ color: '#1890ff' }} />}
          placeholder={t('register.usernamePlaceholder')}
          disabled={loading || authLoading}
        />
      </Form.Item>

      {/* 邮箱输入 */}
      <Form.Item
        name="email"
        label={t('register.email')}
        rules={[
          { required: true, message: t('register.emailRequired') },
          { type: 'email', message: t('register.emailInvalid') }
        ]}
      >
        <Input
          size="large"
          prefix={<MailOutlined style={{ color: '#1890ff' }} />}
          placeholder={t('register.emailPlaceholder')}
          disabled={loading || authLoading}
        />
      </Form.Item>

      {/* 密码输入 */}
      <Form.Item
        name="password"
        label={t('register.password')}
        rules={[
          { required: true, message: t('register.passwordRequired') },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve()
              }
              const { isValid, message: errorMsg } = validatePasswordStrength(value)
              return isValid ? Promise.resolve() : Promise.reject(new Error(errorMsg))
            }
          }
        ]}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined style={{ color: '#1890ff' }} />}
          placeholder={t('register.passwordPlaceholder')}
          disabled={loading || authLoading}
        />
      </Form.Item>

      {/* 确认密码 */}
      <Form.Item
        name="confirmPassword"
        label={t('register.confirmPassword')}
        dependencies={['password']}
        rules={[
          { required: true, message: t('register.confirmPasswordRequired') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(new Error(t('register.confirmPasswordMismatch')))
            }
          })
        ]}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined style={{ color: '#1890ff' }} />}
          placeholder={t('register.confirmPasswordPlaceholder')}
          disabled={loading || authLoading}
        />
      </Form.Item>

      {/* 密码强度提示 */}
      <div style={{ marginBottom: '24px', padding: '12px', background: '#f6ffed', borderRadius: '6px' }}>
        <Text type="secondary">
          <strong>{t('register.passwordRequirements')}</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>{t('register.passwordRequirement1')}</li>
            <li>{t('register.passwordRequirement2')}</li>
            <li>{t('register.passwordRequirement3')}</li>
            <li>{t('register.passwordRequirement4')}</li>
          </ul>
        </Text>
      </div>
    </>
  )

  // 公司信息步骤
  const renderCompanyStep = () => (
    <>
      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '16px' }}>
          <ShopOutlined /> {t('register.companyInfo')}
        </Text>
      </div>
      <Paragraph type="secondary" style={{ marginBottom: '20px', fontSize: '14px' }}>
        {t('register.companyDescription')}
      </Paragraph>

      {/* 公司名称 */}
      <Form.Item
        name="company_name"
        label={t('register.companyName')}
        rules={[
          { required: true, message: t('register.companyNameRequired') },
          { min: 1, message: t('register.companyNameMinLength') },
          { max: 255, message: t('register.companyNameMaxLength') }
        ]}
      >
        <Input
          size="large"
          prefix={<ShopOutlined style={{ color: '#1890ff' }} />}
          placeholder={t('register.companyNamePlaceholder')}
          disabled={loading || authLoading}
        />
      </Form.Item>

      {/* 公司描述 */}
      <Form.Item
        name="company_description"
        label={t('register.companyDescriptionField')}
        rules={[
          { max: 1000, message: t('register.companyDescriptionMaxLength') }
        ]}
      >
        <Input.TextArea
          size="large"
          placeholder={t('register.companyDescriptionPlaceholder')}
          rows={4}
          disabled={loading || authLoading}
          showCount
          maxLength={1000}
        />
      </Form.Item>

      {/* 管理员权限说明 */}
      <div style={{ marginBottom: '24px', padding: '12px', background: '#e6f7ff', borderRadius: '6px' }}>
        <Text type="secondary">
          <strong>{t('register.adminPermissions')}</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>{t('register.adminPermission1')}</li>
            <li>{t('register.adminPermission2')}</li>
            <li>{t('register.adminPermission3')}</li>
            <li>{t('register.adminPermission4')}</li>
          </ul>
        </Text>
      </div>
    </>
  )

  // 服务条款确认
  const renderTermsAgreement = () => (
    <>
      <Divider />
      <Form.Item
        name="agreeTerms"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value ? Promise.resolve() : Promise.reject(new Error(t('register.agreeTermsRequired')))
          }
        ]}
        style={{ marginTop: '24px' }}
      >
        <Checkbox>
          <Trans
            i18nKey="register.agreeTerms"
            ns="pages"
            components={{
              termsLink: <Link to="/terms" style={{ color: '#1890ff' }} />,
              privacyLink: <Link to="/privacy" style={{ color: '#1890ff' }} />
            }}
          />
        </Checkbox>
      </Form.Item>
    </>
  )

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '50px 20px' }}>
        <Row justify="center" align="middle" style={{ minHeight: 'calc(100vh - 100px)' }}>
          <Col xs={24} sm={22} md={20} lg={18} xl={16}>
            <AppCard
              borderless
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}
            >
              {/* 注册标题 */}
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Image
                    src="/static/logo.svg"
                    alt="Logo"
                    preview={false}
                    width={64}
                    height={64}
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
                  {t('register.title')}
                </Title>
                <Paragraph type="secondary">
                  {t('register.subtitle')}
                </Paragraph>
              </div>


              {/* 错误提示 */}
              {error && (
                <Alert
                  message={t('register.registerFailed')}
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                  style={{ marginBottom: '24px' }}
                />
              )}

              {/* 注册表单 */}
              <Form
                form={form}
                name="register"
                layout="vertical"
                requiredMark="optional"
                onFinish={handleSubmit}
                onFinishFailed={handleFinishFailed}
                autoComplete="off"
                style={{ maxWidth: '600px', margin: '0 auto' }}
              >
                {renderAccountStep()}
                <Divider />
                {renderCompanyStep()}
                {renderTermsAgreement()}

                {/* 注册按钮 */}
                <Form.Item style={{ marginTop: '40px', textAlign: 'center' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading || authLoading}
                    style={{
                      height: '48px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                      minWidth: '200px'
                    }}
                  >
                    {loading || authLoading ? t('register.registering') : t('register.registerButton')}
                  </Button>
                </Form.Item>
              </Form>

              <Divider>{t('register.alreadyHaveAccount')}</Divider>

              {/* 登录引导 */}
              <div style={{ textAlign: 'center' }}>
                <Space direction="vertical" size="middle">
                  <Text type="secondary">{t('register.alreadyHaveAccountText')}</Text>
                  <Button
                    type="default"
                    size="large"
                    onClick={handleGoToLogin}
                    style={{
                      borderRadius: '8px',
                      borderColor: '#52c41a',
                      color: '#52c41a'
                    }}
                  >
                    {t('register.loginButton')}
                  </Button>
                </Space>
              </div>

              {/* 服务条款和隐私政策 */}
              <div style={{ marginTop: '32px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <Trans
                    i18nKey="register.terms"
                    ns="pages"
                    components={{
                      termsLink: <Link to="/terms" style={{ color: '#1890ff' }} />,
                      privacyLink: <Link to="/privacy" style={{ color: '#1890ff' }} />
                    }}
                  />
                </Text>
              </div>
            </AppCard>

            {/* 系统信息 */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Text type="secondary">
                {t('register.copyright')}
              </Text>
              <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('register.support')}
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  )
}

export default RegisterPage